import { Fragment, useEffect, useState, type FormEvent } from 'react';
import { api, type UserRole, type UserRow, type UserStatus } from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import { SecretReveal } from '../components/SecretReveal.js';
import { LoginTokensPanel } from '../components/LoginTokensPanel.js';

function errText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function UsersPage({ currentUserId }: { currentUserId?: string }): JSX.Element {
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('member');
  const [initialPassword, setInitialPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [invited, setInvited] = useState<{ email: string; setupUrl: string | null } | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  async function load(): Promise<void> {
    try {
      setUsers(await api.listUsers());
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onInvite(e: FormEvent): Promise<void> {
    e.preventDefault();
    const em = email.trim().toLowerCase();
    if (!em || busy) return;
    setBusy(true);
    setError(null);
    setInvited(null);
    try {
      const r = await api.inviteUser({
        email: em,
        role,
        name: name.trim() || undefined,
        password: initialPassword || undefined,
      });
      setInvited({ email: r.email, setupUrl: r.setupUrl });
      setEmail('');
      setName('');
      setInitialPassword('');
      await load();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  async function patch(id: string, p: { role?: UserRole; status?: UserStatus }): Promise<void> {
    setBusy(true);
    try {
      await api.updateUser(id, p);
      await load();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p className="eyebrow">Workspace</p>
      <h1 className="page-title">Admin</h1>
      <p className="page-subtitle">
        Manage team members, passwords and personal login tokens.
      </p>

      <div className="section-stack">
      <section className="card">
        <div className="card__head">
          <div>
            <h2 className="card__title">
              Team members
              {users && <span className="count-chip">{users.filter((u) => u.status === 'active').length} active</span>}
            </h2>
            <p className="card__sub">Invite teammates and control roles and sign-in methods.</p>
          </div>
        </div>

      <form onSubmit={(e) => void onInvite(e)} style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', padding: 16, background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
        <div className="form-row" style={{ flex: 2, minWidth: 220 }}>
          <label className="form-label" htmlFor="inviteEmail">Email</label>
          <input id="inviteEmail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" required />
        </div>
        <div className="form-row" style={{ flex: 1, minWidth: 150 }}>
          <label className="form-label" htmlFor="inviteName">Name</label>
          <input id="inviteName" value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
        </div>
        <div className="form-row" style={{ width: 130 }}>
          <label className="form-label" htmlFor="inviteRole">Role</label>
          <select id="inviteRole" value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div className="form-row" style={{ flex: 1, minWidth: 180 }}>
          <label className="form-label" htmlFor="invitePw">Initial password</label>
          <input id="invitePw" type="password" autoComplete="new-password" value={initialPassword} onChange={(e) => setInitialPassword(e.target.value)} placeholder="Optional, else invite link" />
        </div>
        <button type="submit" className="btn" style={{ height: 40 }} disabled={busy || !email.trim()}>
          {busy ? 'Saving…' : initialPassword ? 'Create user' : '+ Invite user'}
        </button>
      </form>

      {invited && (
        <div style={{ marginBottom: 16 }}>
          {invited.setupUrl ? (
            <SecretReveal
              label={`Invite link for ${invited.email}`}
              value={invited.setupUrl}
              note="Send this link privately. It lets them set a password and sign in. Valid for 7 days, one use."
              onDismiss={() => setInvited(null)}
            />
          ) : (
            <div className="form-success">{invited.email} was created with the password you set. They can sign in now.</div>
          )}
        </div>
      )}

      {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}

      {!users && !error && (
        <div className="empty"><span className="spinner" /> Loading users&hellip;</div>
      )}

      {users && (
        <div className="table-wrap">
        <table className="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Status</th>
              <th>Sign-in</th>
              <th>Last sign-in</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <Fragment key={u.id}>
                <tr>
                  <td>
                    <div className="user-cell">
                      <span className="avatar">{initialsOf(u.name || u.email)}</span>
                      <div>
                        <div style={{ fontWeight: 600 }}>{u.name || u.email}</div>
                        <div className="form-help">{u.name ? u.email : u.id === currentUserId ? 'You' : ''}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <select
                      value={u.role}
                      disabled={busy || u.id === currentUserId}
                      onChange={(e) => void patch(u.id, { role: e.target.value as UserRole })}
                      style={{ width: 'auto' }}
                    >
                      <option value="member">member</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td>
                    <span className={`status-badge status-badge--${u.status === 'active' ? 'complete' : u.status === 'invited' ? 'active' : 'failed'}`}>
                      <span className="status-badge__dot" />
                      {u.status === 'invited' ? 'Invited (pending)' : u.status.charAt(0).toUpperCase() + u.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <span className={`pill${u.hasPassword ? ' pill--ok' : ''}`}>{u.hasPassword ? 'Password' : 'No password'}</span>
                      {u.loginTokens > 0 && <span className="pill pill--ok">{u.loginTokens} token{u.loginTokens === 1 ? '' : 's'}</span>}
                    </div>
                  </td>
                  <td className="form-help">{u.lastLoginAt ? formatDate(u.lastLoginAt) : 'Never'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setOpenId(openId === u.id ? null : u.id)}
                      >
                        {openId === u.id ? 'Close' : 'Manage'}
                      </button>
                      {u.id !== currentUserId && (u.status !== 'disabled' ? (
                        <button type="button" className="btn btn--danger-ghost btn--sm" disabled={busy} onClick={() => void patch(u.id, { status: 'disabled' })}>
                          Disable
                        </button>
                      ) : (
                        <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={() => void patch(u.id, { status: u.hasPassword ? 'active' : 'invited' })}>
                          Enable
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
                {openId === u.id && (
                  <tr className="user-detail">
                    <td colSpan={6}>
                      <UserDetail user={u} onChanged={() => void load()} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        </div>
      )}
      </section>

      <section className="card">
        <div className="card__head">
          <div>
            <h2 className="card__title">My login tokens</h2>
            <p className="card__sub">Personal tokens that sign you straight into your own account.</p>
          </div>
        </div>
        <div className="callout">
          <div>
            <strong>Treat tokens like passwords</strong>
            Each token is shown once. One token is usually all you need; revoke any you no longer use.
          </div>
        </div>
        <LoginTokensPanel
          load={() => api.myTokens()}
          create={(label, token) => api.createMyToken(label, token)}
          revoke={(id) => api.revokeMyToken(id)}
        />
      </section>
      </div>
    </div>
  );
}

function initialsOf(s: string): string {
  const base = s.includes('@') ? s.split('@')[0]! : s;
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0]![0]}${parts[1]![0]}` : base.slice(0, 2)).toUpperCase();
}

function UserDetail({ user, onChanged }: { user: UserRow; onChanged: () => void }): JSX.Element {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [link, setLink] = useState<string | null>(null);

  async function onSetPassword(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.setUserPassword(user.id, password);
      setPassword('');
      setMsg({ ok: true, text: 'Password saved. Their other sessions were signed out.' });
      onChanged();
    } catch (err) {
      setMsg({ ok: false, text: errText(err) });
    } finally {
      setBusy(false);
    }
  }

  async function onLink(): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.userSetupLink(user.id);
      setLink(r.setupUrl);
    } catch (err) {
      setMsg({ ok: false, text: errText(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="user-detail__grid" style={{ padding: '6px 0' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div className="panel__title" style={{ margin: 0 }}>Password</div>
        <form className="inline-form" onSubmit={(e) => void onSetPassword(e)}>
          <input type="email" autoComplete="username" value={user.email} readOnly hidden />
          <div className="form-row">
            <label className="form-label" htmlFor={`pw-${user.id}`}>{user.hasPassword ? 'Reset password' : 'Set password'}</label>
            <input id={`pw-${user.id}`} type="password" autoComplete="new-password" minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 10 characters" />
          </div>
          <button type="submit" className="btn" disabled={busy || password.length < 10}>Save</button>
        </form>
        <div>
          <button type="button" className="btn btn--ghost btn--sm" disabled={busy || user.status === 'disabled'} onClick={() => void onLink()}>
            Create set-password link
          </button>
        </div>
        {link && (
          <SecretReveal
            label="Set-password link"
            value={link}
            note="Send privately. One use, valid 7 days. Older links for this user stop working."
            onDismiss={() => setLink(null)}
          />
        )}
        {msg && <div className={msg.ok ? 'form-success' : 'form-error'}>{msg.text}</div>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div className="panel__title" style={{ margin: 0 }}>Personal login tokens</div>
        <LoginTokensPanel
          forEmail={user.email}
          load={() => api.userTokens(user.id)}
          create={async (label, token) => {
            const r = await api.createUserToken(user.id, label, token);
            onChanged();
            return r;
          }}
          revoke={async (id) => {
            await api.revokeUserToken(user.id, id);
            onChanged();
          }}
        />
      </div>
    </div>
  );
}
